/**
 * LayoutTabsBar — the Model | layouts | + strip at the bottom edge of the
 * 2D shell, directly above the command line (Faz 3.7 — G6, the OCS
 * statusbar-embedded space_tab moved to its own row in yap's shell).
 *
 * Behaviors: click switches space, double-click starts inline rename,
 * right-click opens Rename / Delete / New Layout, + creates a layout.
 * Visibility: useViewStore.layoutTabsVisible (LAYOUTTAB 0/1 — View tab).
 * Drag-reorder is Faz 10 scope; order stays document order for now.
 */

import {
    Box,
    Button,
    Icon,
    Input,
    MenuItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { Check, FileText, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayoutStore } from '../../../state/useLayoutStore';
import { useDocumentStore } from '../../../state/useDocumentStore';

export function LayoutTabsBar() {
    const { t } = useTranslation();
    const layouts = useLayoutStore((s) => s.layouts);
    const activeId = useLayoutStore((s) => s.activeId);
    const setActive = useLayoutStore((s) => s.setActive);
    const renameLayout = useLayoutStore((s) => s.renameLayout);
    const addLayout = useLayoutStore((s) => s.addLayout);
    const removeLayout = useLayoutStore((s) => s.removeLayout);
    const markDirty = useDocumentStore((s) => s.markDirty);

    // Inline rename (double-click).
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const renameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (renamingId) renameRef.current?.focus();
    }, [renamingId]);

    // Custom right-click menu (Rename / Delete / New Layout).
    const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; layoutId: string } | null>(null);

    // Delete confirmation (acceptance: "silme onaylı").
    const { isOpen: confirmOpen, onOpen: openConfirm, onClose: closeConfirm } = useDisclosure();
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

    const commitRename = () => {
        if (renamingId && renameValue.trim()) {
            renameLayout(renamingId, renameValue.trim());
            markDirty();
        }
        setRenamingId(null);
    };

    const confirmDelete = () => {
        if (deleteTarget) removeLayout(deleteTarget);
        markDirty();
        setDeleteTarget(null);
        closeConfirm();
    };

    return (
        <Box
            h="24px"
            display="flex"
            alignItems="stretch"
            px={1}
            bg="bg.toolbar"
            borderTop="1px solid"
            borderColor="border.base"
            flexShrink={0}
            position="relative"
            zIndex={0}
        >
            {/* MODEL tab (always first, active when activeId is null). */}
            <Box
                as="button"
                role="tab"
                aria-selected={activeId === null}
                onClick={() => setActive(null)}
                display="flex"
                alignItems="center"
                gap="4px"
                px={2}
                mx="2px"
                fontSize="11px"
                fontWeight={activeId === null ? 'semibold' : 'normal'}
                color={activeId === null ? 'text.primary' : 'text.secondary'}
                borderBottom="2px solid"
                borderColor={activeId === null ? 'brand.500' : 'transparent'}
                _hover={{ color: 'text.primary', bg: 'bg.hover' }}
                flexShrink={0}
                whiteSpace="nowrap"
            >
                <Icon as={FileText} boxSize="11px" color="icon.base" />
                {t('ribbon.layoutTabs.model')}
            </Box>

            {/* Layout tabs — horizontal scroll strip. */}
            <Box
                flex="1"
                minWidth={0}
                display="flex"
                alignItems="stretch"
                overflowX="auto"
                overflowY="hidden"
                sx={{
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { height: '0px', display: 'none' },
                }}
            >
                {layouts.map((layout) => {
                    const isActive = layout.id === activeId;
                    const isRenaming = layout.id === renamingId;
                    return isRenaming ? (
                        <Box key={layout.id} display="flex" alignItems="center" px={1} flexShrink={0}>
                            <Input
                                ref={renameRef}
                                size="xs"
                                h="18px"
                                w="110px"
                                p={1}
                                fontSize="11px"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onBlur={commitRename}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitRename();
                                    if (e.key === 'Escape') setRenamingId(null);
                                }}
                                borderColor="brand.500"
                                color="text.primary"
                            />
                        </Box>
                    ) : (
                        <Box
                            key={layout.id}
                            as="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActive(layout.id)}
                            onDoubleClick={() => {
                                setRenamingId(layout.id);
                                setRenameValue(layout.name);
                            }}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                setCtxMenu({ x: e.clientX, y: e.clientY, layoutId: layout.id });
                            }}
                            display="flex"
                            alignItems="center"
                            px={2}
                            fontSize="11px"
                            fontWeight={isActive ? 'semibold' : 'normal'}
                            color={isActive ? 'text.primary' : 'text.secondary'}
                            borderBottom="2px solid"
                            borderColor={isActive ? 'brand.500' : 'transparent'}
                            _hover={{ color: 'text.primary', bg: 'bg.hover' }}
                            flexShrink={0}
                            whiteSpace="nowrap"
                        >
                            {layout.name}
                        </Box>
                    );
                })}
            </Box>

            {/* + new layout (fixed right slot). */}
            <Box display="flex" alignItems="center" flexShrink={0} px={1}>
                <Icon
                    as={Plus}
                    boxSize="13px"
                    color="icon.base"
                    cursor="pointer"
                    onClick={() => {
                        const created = addLayout();
                        if (created) setActive(created.id);
                        markDirty();
                    }}
                    _hover={{ color: 'text.primary' }}
                />
            </Box>

            {/* Right-click context menu (Rename / Delete / New Layout). */}
            {ctxMenu && (
                <>
                    <Box
                        position="fixed"
                        inset={0}
                        zIndex={20}
                        onClick={() => setCtxMenu(null)}
                        onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
                    />
                    <Box
                        position="fixed"
                        left={`${Math.min(ctxMenu.x, window.innerWidth - 170)}px`}
                        top={`${Math.min(ctxMenu.y, window.innerHeight - 110)}px`}
                        zIndex={21}
                        minW="160px"
                        py={1}
                        bg="bg.panel"
                        border="1px solid"
                        borderColor="border.base"
                        borderRadius="4px"
                        boxShadow="md"
                    >
                        <MenuItem
                            fontSize="12px"
                            color="text.primary"
                            onClick={() => {
                                setRenamingId(ctxMenu.layoutId);
                                setRenameValue(layouts.find((l) => l.id === ctxMenu.layoutId)?.name ?? '');
                                setCtxMenu(null);
                            }}
                        >
                            {t('ribbon.layoutTabs.rename')}
                        </MenuItem>
                        <MenuItem
                            fontSize="12px"
                            color="text.primary"
                            onClick={() => {
                                setDeleteTarget(ctxMenu.layoutId);
                                setCtxMenu(null);
                                openConfirm();
                            }}
                        >
                            {t('ribbon.layoutTabs.delete')}
                        </MenuItem>
                        <MenuItem
                            fontSize="12px"
                            color="text.primary"
                            onClick={() => {
                                addLayout();
                                markDirty();
                                setCtxMenu(null);
                            }}
                        >
                            {t('ribbon.layoutTabs.new')}
                        </MenuItem>
                    </Box>
                </>
            )}

            {/* Delete confirmation modal. */}
            <Modal isOpen={confirmOpen} onClose={closeConfirm} size="sm">
                <ModalOverlay />
                <ModalContent bg="bg.panel" borderColor="border.base">
                    <ModalHeader fontSize="md" color="text.primary">
                        {t('ribbon.layoutTabs.deleteConfirmTitle')}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="text.secondary">
                            {t('ribbon.layoutTabs.deleteConfirmBody', {
                                name: layouts.find((l) => l.id === deleteTarget)?.name ?? '',
                            })}
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button size="sm" variant="ghost" onClick={closeConfirm}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            colorScheme="red"
                            leftIcon={<Icon as={Check} boxSize="12px" />}
                            onClick={confirmDelete}
                        >
                            {t('ribbon.layoutTabs.deleteConfirmOk')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
